import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'default' | 'secondary' | 'outline' | 'ghost' | 'destructive'
  size?: 'sm' | 'default'
  loading?: boolean
}

const variants: Record<NonNullable<ButtonProps['variant']>, string> = {
  default:
    'bg-enterprise-indigo text-enterprise-on-accent enabled:hover:bg-enterprise-strong border border-transparent shadow-none disabled:bg-enterprise-raised disabled:text-enterprise-subtle disabled:border-enterprise-border motion-reduce:transition-none',
  secondary:
    'bg-enterprise-surface text-enterprise-text border border-enterprise-border enabled:hover:bg-enterprise-raised enabled:hover:border-enterprise-border-strong disabled:bg-enterprise-raised disabled:text-enterprise-subtle disabled:border-enterprise-border motion-reduce:transition-none',
  outline:
    'border border-enterprise-border bg-enterprise-bg enabled:hover:bg-enterprise-surface enabled:hover:border-enterprise-dim text-enterprise-text disabled:bg-enterprise-raised disabled:text-enterprise-subtle disabled:border-enterprise-border motion-reduce:transition-none',
  ghost:
    'border border-transparent enabled:hover:bg-enterprise-surface text-enterprise-muted enabled:hover:text-enterprise-text disabled:text-enterprise-subtle disabled:bg-transparent motion-reduce:transition-none',
  destructive:
    'bg-enterprise-danger text-enterprise-on-accent enabled:hover:bg-enterprise-danger-strong border border-transparent shadow-none disabled:bg-enterprise-raised disabled:text-enterprise-subtle disabled:border-enterprise-border motion-reduce:transition-none',
}

const sizes: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'h-8 px-4 text-sm rounded-full',
  default: 'h-[var(--control-h)] px-5 py-2 text-sm rounded-full',
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'default', loading = false, disabled, children, ...props }, ref) => {
    const isDisabled = Boolean(disabled || loading)
    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center whitespace-nowrap text-sm font-medium tracking-tight motion-transition-fast motion-safe:active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo focus-visible:ring-offset-2 focus-visible:ring-offset-enterprise-bg disabled:cursor-not-allowed motion-reduce:transition-none',
          variants[variant],
          sizes[size],
          loading ? 'cursor-wait' : null,
          className,
        )}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        aria-disabled={isDisabled || undefined}
        {...props}
      >
        {loading ? (
          <Loader2 className="mr-2 h-4 w-4 shrink-0 motion-safe:animate-spin motion-reduce:animate-none" aria-hidden="true" />
        ) : null}
        {children}
      </button>
    )
  },
)
Button.displayName = 'Button'
